import axios from 'axios';
import { Prospect } from '../../types/savvydata.ts';
import mockApi from './mockApi.ts';

// Use mock API for the demo
const useRealApi = false;

// Only for real API usage (we're using mock for demo)
const api = axios.create({
  baseURL: 'http://localhost:8000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Prospects endpoints
export const prospectsApi = {
  getProspect: async (id: string) => {
    if (useRealApi) {
      const response = await api.get(`/prospects/${id}`);
      return response.data;
    } else {
      return mockApi.prospects.enrichProspect(id);
    }
  },
  
  createProspect: async (prospect: Partial<Prospect>) => {
    if (useRealApi) {
      const response = await api.post('/prospects', prospect);
      return response.data;
    } else {
      return mockApi.prospects.createProspect(prospect);
    }
  },
  
  enrichProspect: async (id: string) => {
    if (useRealApi) {
      const response = await api.post(`/prospects/${id}/enrich`);
      return response.data;
    } else {
      return mockApi.prospects.enrichProspect(id);
    }
  },
  
  analyzeProspect: async (id: string) => {
    if (useRealApi) {
      const response = await api.post(`/prospects/${id}/analyze`);
      return response.data;
    } else {
      return mockApi.prospects.enrichProspect(id);
    }
  },
  
  getSimilarProspects: async (id: string, params = {}) => {
    if (useRealApi) {
      const response = await api.get(`/prospects/similar/${id}`, { params });
      return response.data;
    } else {
      return []; // Mock would return an empty array
    }
  },
};

// Email endpoints
export const emailApi = {
  generateEmail: async (prospectId: string, templateType: string) => {
    if (useRealApi) {
      const response = await api.post('/emails/generate', {
        prospect_id: prospectId,
        template_type: templateType
      });
      return response.data;
    } else {
      return mockApi.email.generateEmail(prospectId, templateType);
    }
  },
  
  sendEmail: async (emailData: any) => {
    if (useRealApi) {
      const response = await api.post('/emails/send', emailData);
      return response.data;
    } else {
      return { success: true, message: "Email sent successfully" };
    }
  },
};

// CRM endpoints
export const crmApi = {
  getSalesforceContacts: async (query = {}) => {
    if (useRealApi) {
      const response = await api.get('/crm/salesforce/contacts', { params: query });
      return response.data;
    } else {
      return mockApi.crm.getSalesforceContacts(query);
    }
  },
  
  syncProspectToCRM: async (prospectId: string, crmType: 'salesforce' | 'hubspot') => {
    if (useRealApi) {
      const response = await api.post(`/crm/${crmType}/sync`, { prospect_id: prospectId });
      return response.data;
    } else {
      return { success: true, crm_id: "CRM123456" };
    }
  },
};

// Analytics endpoints
export const analyticsApi = {
  getPersonaAnalysis: async (prospectId: string) => {
    if (useRealApi) {
      const response = await api.get(`/analytics/persona/${prospectId}`);
      return response.data;
    } else {
      return mockApi.analytics.getPersonaAnalysis(prospectId);
    }
  },
  
  getEngagementMetrics: async (prospectId: string) => {
    if (useRealApi) {
      const response = await api.get(`/analytics/engagement/${prospectId}`);
      return response.data;
    } else {
      return mockApi.analytics.getEngagementMetrics(prospectId);
    }
  },
};

export default {
  prospects: prospectsApi,
  email: emailApi,
  crm: crmApi,
  analytics: analyticsApi,
}; 